import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { RoleAccessDirective } from '../../../@core/directives/role-access.directive';
import { FiltersAndPaginationPayloadService } from '../../../@core/services/filters-and-pagination-payload.service';
import { QuantityBreakLabelService } from '../../../@core/services/quantity-break-label.service';
import { OrdersActions } from '../../../@store/orders/orders.actions';
import { OrdersApiResponseItem } from '../../../@store/orders/orders.model';
import {
  selectAllOrders,
  selectExportOrdersBundleError,
  selectExportOrdersBundleLoading,
  selectOrdersError,
  selectOrdersLoading,
  selectOrdersPriceTotal,
  selectOrdersPriceTotalNotShipped,
  selectOrdersTotalCount,
} from '../../../@store/orders/orders.selectors';
import { selectIsOrdersManager } from '../../../@store/user/user.selectors';
import { CONFIG } from '../../../config';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';
import { ArchiveOrderDialogComponent } from '../manage-orders/archive-order-dialog/archive-order-dialog.component';

@Component({
  selector: 'ciwc-active-orders',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatSelectModule,
    PaginationComponent,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    RouterLink,
    RoleAccessDirective,
  ],
  templateUrl: './active-orders.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveOrdersComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly payloadService = inject(FiltersAndPaginationPayloadService);
  private readonly quantityBreakLabelService = inject(QuantityBreakLabelService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  readonly orders = this.store.selectSignal(selectAllOrders);
  readonly ordersLoading = this.store.selectSignal(selectOrdersLoading);
  readonly ordersError = this.store.selectSignal(selectOrdersError);
  readonly totalOrders = this.store.selectSignal(selectOrdersTotalCount);
  readonly priceTotal = this.store.selectSignal(selectOrdersPriceTotal);
  readonly priceTotalNotShipped = this.store.selectSignal(selectOrdersPriceTotalNotShipped);
  readonly exportOrdersBundleLoading = this.store.selectSignal(selectExportOrdersBundleLoading);
  readonly exportOrdersBundleError = this.store.selectSignal(selectExportOrdersBundleError);
  readonly isOrdersManager = this.store.selectSignal(selectIsOrdersManager);
  readonly CONFIG = CONFIG;

  protected readonly Math = Math;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<OrdersApiResponseItem>;

  selection = new SelectionModel<OrdersApiResponseItem>(true, []);
  dataSource: MatTableDataSource<OrdersApiResponseItem> =
    new MatTableDataSource<OrdersApiResponseItem>();
  displayedColumns: string[] = [
    'select',
    'orderId',
    'description',
    'clientPurchaseOrder',
    'caNumber',
    'promisedDate',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'totalQuantity',
    'orderTotalPrice',
    'orderTotalPriceNotShipped',
    'dateUpdated',
    'actions',
  ];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  pageIndex = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };
  private archivedFilter: 'true' | 'false' | 'archived' | 'all' = 'false';
  @Input() onSaveList = false;

  constructor() {
    effect(() => {
      this.initValues();
      this.hideQuantityAndPriceForManager();
    });
  }

  ngOnInit() {
    this.conditionallyShowColumns();
    this.setPagination();
    this.route.data.subscribe((data) => {
      this.archivedFilter = data['archived'] ?? 'false';

      this.fetchData();
    });
  }

  archiveOrder(order: OrdersApiResponseItem): void {
    const dialogRef = this.dialog.open(ArchiveOrderDialogComponent, {
      data: { order, isArchived: order.archived },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        const path = result.isArchived ? 'archived-orders' : 'active-orders';
        this.router.navigate(['/orders', path]).then((success: boolean): void => {
          if (success) {
            console.log('Navigation successful after archiving!');
          } else {
            console.warn('Navigation failed after archiving!');
          }
        });
      }
    });
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    this.payloadService.updateSortFromMaterial(sort);

    this.store.dispatch(
      OrdersActions.loadOrdersList({
        payload: { ...this.payloadService.combinedPayload, archived: this.archivedFilter },
      }),
    );
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    this.payloadService.updatePaginationPayload({
      offset: this.pageIndex + 1,
      limit: this.pageSize,
    });

    this.store.dispatch(
      OrdersActions.loadOrdersList({
        payload: { ...this.payloadService.combinedPayload, archived: this.archivedFilter },
      }),
    );
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;

    return numSelected === numRows;
  }

  checkboxLabel(row?: OrdersApiResponseItem): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.orderId}`;
  }

  private initValues(): void {
    let ordersTableData: OrdersApiResponseItem[] = this.orders() as OrdersApiResponseItem[];

    // Adding isPastDue property to each order for red coloring past due orders
    ordersTableData = ordersTableData?.map((row: OrdersApiResponseItem) => ({
      ...row,
      isPastDue: this.isPastDue(row.dueDate),
    }));

    this.dataSource.data = ordersTableData || [];
  }

  private fetchData(): void {
    this.payloadService.updateSortFromMaterial(this.currentSort);

    this.store.dispatch(
      OrdersActions.loadOrdersList({
        payload: {
          ...this.payloadService.combinedPayload,
          archived: this.archivedFilter,
          sort: '-dateCreated',
        },
      }),
    );
  }

  /**
   * This method is used to conditionally show or hide columns based on the page we are on.
   * If we are on the save list page, we remove the select and actions columns.
   */
  private conditionallyShowColumns() {
    // remove select and actions columns if we are on save list page
    if (this.onSaveList) {
      this.displayedColumns = this.displayedColumns.filter(
        (column: string): boolean => column !== 'select' && column !== 'actions',
      );
    }
  }

  /**
   * Checks if the given due date is past due.
   * @param dueDate
   * @returns {boolean}
   */
  isPastDue(dueDate: string): boolean {
    if (!dueDate) return false;
    // Parse as local or UTC, fallback to Date constructor
    const due = new Date(dueDate.replace(' ', 'T'));
    return due.getTime() < Date.now();
  }

  get hasSelection(): boolean {
    return this.selection.selected.length > 0;
  }

  exportOrdersBundle() {
    if (!this.hasSelection || this.exportOrdersBundleLoading()) return;
    const orderIds = this.selection.selected.map((o) => o.id).join(',');
    this.store.dispatch(OrdersActions.exportOrdersBundle({ payload: { orders: orderIds } }));
  }

  private hideQuantityAndPriceForManager(): void {
    if (this.isOrdersManager()) {
      this.displayedColumns = this.displayedColumns.filter(
        (column) => column !== 'orderTotalPrice' && column !== 'orderTotalPriceNotShipped',
      );
    }
  }

  private setPagination(): void {
    this.payloadService.paginationPayload$.subscribe((value) => {
      if (!value) return;

      this.pageSize = value.limit;
      this.pageIndex = value.offset - 1;
    });
  }
}
